import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import { PLANS, formatNumber, ERROR_MESSAGES, SUCCESS_MESSAGES } from '../utils/constants';
import '../styles/StakingForm.css';

const StakingForm = ({ 
  stakingContract, 
  tokenContract, 
  currentNetwork, 
  account, 
  isNewUser, 
  refreshData, 
  setLoading, 
  loading,
  defaultReferralId = ''
}) => {
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [stakeAmount, setStakeAmount] = useState('');
  const [referrerId, setReferrerId] = useState(defaultReferralId);
  const [tokenBalance, setTokenBalance] = useState('0');
  const [tokenAllowance, setTokenAllowance] = useState('0');
  const [tokenPrice, setTokenPrice] = useState('2.00');
  const [needsApproval, setNeedsApproval] = useState(false);
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(false);

  // Update referral ID when defaultReferralId changes
  useEffect(() => {
    if (defaultReferralId && !referrerId) {
      setReferrerId(defaultReferralId);
    }
  }, [defaultReferralId]);

  // Load token price
  useEffect(() => {
    if (stakingContract) {
      loadTokenPrice();
    }
  }, [stakingContract]);

  // Load token data
  useEffect(() => {
    if (tokenContract && account) {
      loadTokenData();
    }
  }, [tokenContract, account, stakeAmount, tokenPrice]);

  const loadTokenPrice = async () => {
    try {
      const price = await stakingContract.getTokenPrice();
      setTokenPrice(formatNumber(parseFloat(price.toString()) / 1e18, 2));
    } catch (error) {
      console.error('Failed to get token price:', error);
      setTokenPrice('2.00');
    }
  };

  const loadTokenData = async () => {
    try {
      const [balance, allowance] = await Promise.all([
        tokenContract.balanceOf(account),
        tokenContract.allowance(account, currentNetwork.contracts.staking)
      ]);
      
      setTokenBalance(ethers.utils.formatEther(balance));
      setTokenAllowance(ethers.utils.formatEther(allowance));
      
      if (stakeAmount && tokenPrice) {
        // Calculate required tokens for USD amount
        const requiredTokens = parseFloat(stakeAmount) / parseFloat(tokenPrice);
        const requiredTokensWei = ethers.utils.parseEther(requiredTokens.toString());
        setNeedsApproval(allowance.lt(requiredTokensWei));
      }
    } catch (error) {
      console.error('Failed to load token data:', error);
    }
  };

  // Filter plans based on withdrawal frequency
  const filteredPlans = PLANS.filter(plan => {
    if (filterType === 'all') return true;
    if (filterType === 'daily') return plan.frequency === 'Daily';
    if (filterType === 'monthly') return plan.frequency === 'Monthly';
    if (filterType === 'yearly') return plan.frequency === 'Yearly';
    if (filterType === 'end') return plan.frequency === 'End Only';
    return true;
  });

  // Get category display name
  const getCategoryDisplayName = () => {
    switch (filterType) {
      case 'all': return 'All Investment Plans';
      case 'daily': return 'Daily Withdrawal Plans';
      case 'monthly': return 'Monthly Withdrawal Plans';
      case 'yearly': return 'Yearly Withdrawal Plans';
      case 'end': return 'End-Only Withdrawal Plans';
      default: return 'Investment Plans';
    }
  };

  const getPlanColorScheme = (planId) => {
    const colors = {
      1: { primary: '#3b82f6', secondary: '#2563eb', bg: 'rgba(59, 130, 246, 0.1)' },
      2: { primary: '#10b981', secondary: '#059669', bg: 'rgba(16, 185, 129, 0.1)' },
      3: { primary: '#f59e0b', secondary: '#d97706', bg: 'rgba(245, 158, 11, 0.1)' },
      4: { primary: '#ef4444', secondary: '#dc2626', bg: 'rgba(239, 68, 68, 0.1)' },
      5: { primary: '#8b5cf6', secondary: '#7c3aed', bg: 'rgba(139, 92, 246, 0.1)' },
      6: { primary: '#06b6d4', secondary: '#0891b2', bg: 'rgba(6, 182, 212, 0.1)' },
      7: { primary: '#84cc16', secondary: '#65a30d', bg: 'rgba(132, 204, 22, 0.1)' },
      8: { primary: '#f97316', secondary: '#ea580c', bg: 'rgba(249, 115, 22, 0.1)' },
      9: { primary: '#ec4899', secondary: '#db2777', bg: 'rgba(236, 72, 153, 0.1)' },
      10: { primary: '#14b8a6', secondary: '#0d9488', bg: 'rgba(20, 184, 166, 0.1)' },
      11: { primary: '#64748b', secondary: '#475569', bg: 'rgba(100, 116, 139, 0.1)' },
      12: { primary: '#0ea5e9', secondary: '#0284c7', bg: 'rgba(14, 165, 233, 0.1)' },
      13: { primary: '#d946ef', secondary: '#c026d3', bg: 'rgba(217, 70, 239, 0.1)' },
      14: { primary: '#22c55e', secondary: '#16a34a', bg: 'rgba(34, 197, 94, 0.1)' }
    };
    return colors[planId] || colors[1];
  };

  const calculateTotalReturn = (plan) => {
    // Hardcoded realistic multipliers based on plan characteristics
    const multipliers = {
      1: { multiplier: '2.0x', description: 'Double your investment' },
      2: { multiplier: '2.5x', description: '150% total return' }, // Your 2.5% monthly example
      3: { multiplier: '2.08x', description: '80% total return' },
      4: { multiplier: '3.2x', description: '220% total return' },
      5: { multiplier: '2.2x', description: '60% total return' },
      6: { multiplier: '2.4x', description: '180% total return' },
      7: { multiplier: '4x', description: '40% total return' },
      8: { multiplier: '4.2x', description: '120% total return' },
      9: { multiplier: '1.3x', description: '250% total return' },
      10: { multiplier: '1.3x', description: '90% total return' },
      11: { multiplier: 'Referral', description: 'No staking rewards — 6% referral + levels' },
      12: { multiplier: 'Referral', description: 'No staking rewards — 25% referral + levels' },
      13: { multiplier: 'Referral', description: 'No staking rewards — 50% referral + levels' },
      14: { multiplier: '~1.0x', description: '1.66% monthly x 60 (no capital return)' }
    };

    return multipliers[plan.id] || { multiplier: '1.0x', description: 'Invalid plan' };
  };

  const getPlanBadges = (plan) => {
    const badges = [];

    if (plan.requiresReferrals) {
      badges.push({ text: 'Referrals Required', color: '#f59e0b', icon: '👥' });
    }

    if (plan.referralOnly) {
      badges.push({ text: 'Referral / RIA Only', color: '#a855f7', icon: '🔁' });
    }
    
    if (plan.min === 100) {
      badges.push({ text: 'Low Entry', color: '#10b981', icon: '💚' });
    }
    
    if (plan.max >= 25000) {
      badges.push({ text: 'High Limit', color: '#8b5cf6', icon: '💎' });
    }
    
    if (plan.frequency === 'Daily') {
      badges.push({ text: 'Daily Returns', color: '#06b6d4', icon: '🔄' });
    }
    
    if (plan.roiPercentage >= 400) {
      badges.push({ text: 'High Rewards', color: '#ef4444', icon: '🚀' });
    }
    
    return badges;
  };

  const handlePlanSelect = (plan) => {
    setSelectedPlan(plan);
    setShowModal(true);
    setStakeAmount('');
    setErrors({});
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedPlan(null);
    setStakeAmount('');
    setErrors({});
    setSuccess('');
  };

  const validateForm = () => {
    const newErrors = {};

    if (!stakeAmount) {
      newErrors.amount = 'Please enter an amount to stake';
    } else {
      const amount = parseFloat(stakeAmount);
      if (isNaN(amount) || amount <= 0) {
        newErrors.amount = 'Please enter a valid amount';
      } else if (amount < selectedPlan.min) {
        newErrors.amount = `Minimum amount for this plan is ${selectedPlan.min} USD`;
      } else if (amount > selectedPlan.max) {
        newErrors.amount = `Maximum amount for this plan is ${selectedPlan.max} USD`;
      } else {
        // Check if user has enough tokens for the USD amount
        const requiredTokens = amount / parseFloat(tokenPrice);
        if (requiredTokens > parseFloat(tokenBalance)) {
          newErrors.amount = `Insufficient balance. Need ${formatNumber(requiredTokens, 2)} TRAIL (${amount} USD)`;
        }
      }
    }

    if (isNewUser) {
      if (referrerId && referrerId.trim() !== '' && !/^\d+$/.test(referrerId.trim())) {
        newErrors.referrer = 'Referrer ID must be a valid number';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleApprove = async () => {
    if (!tokenContract || !stakeAmount || !tokenPrice) return;

    try {
      setLoading(true);
      setSuccess('');
      setErrors({});

      // Calculate required tokens for USD amount
      const requiredTokens = parseFloat(stakeAmount) / parseFloat(tokenPrice);
      const amount = ethers.utils.parseEther(requiredTokens.toString());
      const tx = await tokenContract.approve(currentNetwork.contracts.staking, amount);
      
      setSuccess('Approval transaction sent. Please wait for confirmation...');
      await tx.wait();
      
      setSuccess(SUCCESS_MESSAGES.APPROVAL_SUCCESS);
      await loadTokenData();
      
    } catch (error) {
      console.error('Approval failed:', error);
      
      if (error.code === 4001) {
        setErrors({ general: ERROR_MESSAGES.USER_REJECTED });
      } else {
        setErrors({ general: 'Approval failed: ' + error.message });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleStake = async () => {
    if (!validateForm() || !stakingContract || !tokenContract) return;

    try {
      setLoading(true);
      setSuccess('');
      setErrors({});

      // Contract expects USD amount in wei
      const usdAmountWei = ethers.utils.parseEther(stakeAmount);
      
      if (needsApproval) {
        await handleApprove();
        return;
      }

      let tx;
      if (isNewUser) {
        const refId = referrerId.trim() || '0';
        tx = await stakingContract.registerAndStake(selectedPlan.id, usdAmountWei, refId);
      } else {
        tx = await stakingContract.stake(selectedPlan.id, usdAmountWei);
      }

      setSuccess('Transaction sent. Please wait for confirmation...');
      await tx.wait();
      
      setSuccess(SUCCESS_MESSAGES.STAKE_SUCCESS);
      
      // Reset form and close modal
      setStakeAmount('');
      setSelectedPlan(null);
      setShowModal(false);
      
      await refreshData();
      
    } catch (error) {
      console.error('Staking failed:', error);
      
      if (error.code === 4001) {
        setErrors({ general: ERROR_MESSAGES.USER_REJECTED });
      } else if (error.message.includes('insufficient funds')) {
        setErrors({ general: ERROR_MESSAGES.INSUFFICIENT_BALANCE });
      } else {
        setErrors({ general: 'Staking failed: ' + error.message });
      }
    } finally {
      setLoading(false);
    }
  };

  const calculatePotentialReturn = () => {
    if (!stakeAmount || !selectedPlan || isNaN(parseFloat(stakeAmount))) return 0;
    
    const amount = parseFloat(stakeAmount);
    const plan = selectedPlan;
    
    // Fixed realistic calculations based on actual plan logic from your contract
    switch (plan.id) {
      case 1: // Basic Staking - 2x after 60 months (end only)
        return amount * 2; // Return only the profit (1x), total will be 2x
        
      case 2: // Regular Monthly 2.5% for 60 months
        // 2.5% monthly for 60 months = 150% total return
        return amount * 1.5; // 150% profit
        
      case 3: // Regular Monthly 3% for 36 months  
        // 3% monthly for 36 months = 108% total return
        return amount * 1.08; // 108% profit
        
      case 4: // Super Daily 0.19% for 1825 days
        // 0.19% daily for 1825 days = 346.75% total return
        return amount * 3.4675; // 346.75% profit
        
      case 5: // Prime Yearly 3.5% for 36 months (yearly withdrawal)
        // 3.5% monthly for 36 months = 126% total return
        return amount * 1.26; // 126% profit
        
      case 6: // Prime End 4% for 36 months (end only)
        // 4% monthly for 36 months = 144% total return
        return amount * 1.44; // 144% profit
        
      case 7: // Best Value 5% for 60 months (end only)
        // 5% monthly for 60 months = 300% total return
        return amount * 3.0; // 300% profit
        
      case 8: // Prime Super Daily 0.19% for 1825 days (end only)
        // Same as Plan 4: 346.75% total return
        return amount * 3.4675; // 346.75% profit
        
      case 9: // Prime Basic 0.072% for 1825 days (end only)
        // 0.072% daily for 1825 days = 131.4% total return
        return amount * 1.314; // 131.4% profit
        
      case 10: // Prime Basic Super 0.072% for 1825 days (daily)
        // Same as Plan 9: 131.4% total return
        return amount * 1.314; // 131.4% profit

      case 11: // Basic Bounty - no staking rewards (referral/RIA only)
      case 12: // Super Basic 1 - no staking rewards (referral/RIA only)
      case 13: // Super Basic 2 - no staking rewards (referral/RIA only)
        return 0; // earnings come from referral + level commissions, not staking ROI

      case 14: // Prime Basic 0 - 1.66% monthly for 60 months, no capital return
        // 1.66% x 60 = 99.6% total return (no capital back)
        return amount * 0.996;

      default:
        return 0; // Fallback for unknown plans
    }
  };

  const setMaxAmount = () => {
    if (!selectedPlan || !tokenPrice) return;
    // Calculate max USD amount based on token balance
    const maxUsdFromBalance = parseFloat(tokenBalance) * parseFloat(tokenPrice);
    const maxPossible = Math.min(maxUsdFromBalance, selectedPlan.max);
    setStakeAmount(maxPossible.toString());
  };

  const getRequiredTokens = () => {
    if (!stakeAmount || !tokenPrice) return 0;
    return parseFloat(stakeAmount) / parseFloat(tokenPrice);
  };

  return (
    <div className="staking-form-container">
      {/* Header */}
      <div className="staking-header">
        <div className="staking-header-content">
          <h1 className="staking-page-title">
            {isNewUser ? 'Register & Start Investing' : 'Make New Investment'}
          </h1>
          <p className="staking-page-subtitle">
            {isNewUser 
              ? 'Create your account and choose your first investment plan'
              : 'Select a plan and stake your tokens to start earning'
            }
          </p>
          <div className="token-price-display">
            <span className="price-label">Current Token Price:</span>
            <span className="price-value">1 TRAIL = ${tokenPrice}</span>
          </div>
        </div>

        {/* Filter Buttons */}
        <div className="staking-filter-section">
          <h3 className="staking-filter-title">Filter Plans by Withdrawal Frequency</h3>
          <p className="staking-filter-subtitle">
            Showing: <strong>{getCategoryDisplayName()}</strong> 
            ({filteredPlans.length} plan{filteredPlans.length !== 1 ? 's' : ''} available)
          </p>
          <div className="staking-filter-buttons">
            <button
              className={`staking-filter-btn ${filterType === 'all' ? 'active' : ''}`}
              onClick={() => setFilterType('all')}
            >
              <span className="staking-filter-icon">📊</span>
              <span>All Plans</span>
            </button>
            <button
              className={`staking-filter-btn ${filterType === 'daily' ? 'active' : ''}`}
              onClick={() => setFilterType('daily')}
            >
              <span className="staking-filter-icon">📅</span>
              <span>Daily</span>
            </button>
            <button
              className={`staking-filter-btn ${filterType === 'monthly' ? 'active' : ''}`}
              onClick={() => setFilterType('monthly')}
            >
              <span className="staking-filter-icon">📆</span>
              <span>Monthly</span>
            </button>
            <button
              className={`staking-filter-btn ${filterType === 'yearly' ? 'active' : ''}`}
              onClick={() => setFilterType('yearly')}
            >
              <span className="staking-filter-icon">🗓️</span>
              <span>Yearly</span>
            </button>
            <button
              className={`staking-filter-btn ${filterType === 'end' ? 'active' : ''}`}
              onClick={() => setFilterType('end')}
            >
              <span className="staking-filter-icon">⏰</span>
              <span>End Only</span>
            </button>
          </div>
        </div>
      </div>

      {/* Notifications */}
      {success && (
        <div className="staking-notification success">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M9 12l2 2 4-4"/>
            <circle cx="12" cy="12" r="10"/>
          </svg>
          <span>{success}</span>
        </div>
      )}

      {errors.general && (
        <div className="staking-notification error">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <line x1="15" y1="9" x2="9" y2="15"/>
            <line x1="9" y1="9" x2="15" y2="15"/>
          </svg>
          <span>{errors.general}</span>
        </div>
      )}

      <div className="staking-content">
        {/* Plans Grid */}
        <div className="staking-plans-section">
          <div className="staking-plans-grid">
            {filteredPlans.map(plan => {
              const colorScheme = getPlanColorScheme(plan.id);
              const totalReturn = calculateTotalReturn(plan);
              const badges = getPlanBadges(plan);

              return (
                <div
                  key={plan.id}
                  className="staking-plan-card"
                  style={{
                    '--primary-color': colorScheme.primary,
                    '--secondary-color': colorScheme.secondary,
                    '--bg-color': colorScheme.bg
                  }}
                >
                  {/* Plan Header */}
                  <div className="staking-plan-header">
                    <div className="staking-plan-number">#{plan.id}</div>
                    <div className="staking-plan-title">
                      <h3>{plan.name}</h3>
                      <div className="staking-plan-roi-badge">{plan.roi}</div>
                    </div>
                  </div>

                  {/* Plan Badges */}
                  {badges.length > 0 && (
                    <div className="staking-plan-badges">
                      {badges.map((badge, index) => (
                        <span 
                          key={index}
                          className="staking-plan-badge"
                          style={{ backgroundColor: badge.color }}
                        >
                          <span className="badge-icon">{badge.icon}</span>
                          {badge.text}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Plan Stats */}
                  <div className="staking-plan-stats">
                    <div className="staking-stat-item">
                      <div className="staking-stat-icon">💰</div>
                      <div className="staking-stat-content">
                        <span className="staking-stat-label">Investment Range</span>
                        <span className="staking-stat-value">
                          ${plan.min} - ${formatNumber(plan.max)} USD
                        </span>
                      </div>
                    </div>

                    <div className="staking-stat-item">
                      <div className="staking-stat-icon">📅</div>
                      <div className="staking-stat-content">
                        <span className="staking-stat-label">Withdrawal</span>
                        <span className="staking-stat-value">{plan.frequency}</span>
                      </div>
                    </div>

                    <div className="staking-stat-item">
                      <div className="staking-stat-icon">⏱️</div>
                      <div className="staking-stat-content">
                        <span className="staking-stat-label">Duration</span>
                        <span className="staking-stat-value">
                          {plan.duration} {plan.duration > 100 ? 'days' : ''}
                        </span>
                      </div>
                    </div>

                    <div className="staking-stat-item">
                      <div className="staking-stat-icon">🎯</div>
                      <div className="staking-stat-content">
                        <span className="staking-stat-label">Total Return</span>
                        <span className="staking-stat-value highlight">{totalReturn.multiplier}</span>
                      </div>
                    </div>
                  </div>

                  {/* Plan Description */}
                  <div className="staking-plan-description">
                    <p>{plan.description}</p>
                  </div>

                  {/* Select Button */}
                  <button 
                    className="staking-select-plan-btn"
                    onClick={() => handlePlanSelect(plan)}
                    disabled={loading}
                  >
                    <span className="btn-icon">🚀</span>
                    Select This Plan
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Investment Modal */}
      {showModal && selectedPlan && (
        <div className="staking-modal-overlay" onClick={handleCloseModal}>
          <div className="staking-modal-content" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="staking-modal-header">
              <div className="staking-form-header">
                <h2>Complete Your Investment</h2>
                <p>Plan: <strong>{selectedPlan.name}</strong> - {selectedPlan.roi} Rewards</p>
              </div>
              <button className="staking-modal-close" onClick={handleCloseModal}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>

            {/* Modal Body */}
            <div className="staking-modal-body">
              <div className="staking-investment-form-card">
                <div className="form-content">
                  {/* Amount Input */}
                  <div className="staking-form-group">
                    <label className="staking-form-label">Investment Amount (USD)</label>
                    <div className="staking-amount-input-container">
                      <div className="staking-input-wrapper">
                        <input
                          type="number"
                          placeholder="Enter USD amount"
                          value={stakeAmount}
                          onChange={(e) => setStakeAmount(e.target.value)}
                          className={`staking-amount-input ${errors.amount ? 'error' : ''}`}
                          min={selectedPlan.min}
                          max={selectedPlan.max}
                          step="0.01"
                        />
                        <span className="staking-input-suffix">USD</span>
                        <button 
                          type="button" 
                          className="staking-max-btn"
                          onClick={setMaxAmount}
                          disabled={loading}
                        >
                          MAX
                        </button>
                      </div>
                      
                      {errors.amount && (
                        <div className="staking-field-error">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"/>
                            <line x1="12" y1="8" x2="12" y2="12"/>
                            <line x1="12" y1="16" x2="12.01" y2="16"/>
                          </svg>
                          {errors.amount}
                        </div>
                      )}
                    </div>

                    <div className="staking-amount-info">
                      <div className="staking-info-row">
                        <span>Your Balance:</span>
                        <span>{formatNumber(tokenBalance, 2)} TRAIL (${formatNumber(parseFloat(tokenBalance) * parseFloat(tokenPrice), 2)})</span>
                      </div>
                      <div className="staking-info-row">
                        <span>Token Price:</span>
                        <span>1 TRAIL = ${tokenPrice}</span>
                      </div>
                      <div className="staking-info-row">
                        <span>Min Amount:</span>
                        <span>${selectedPlan.min}</span>
                      </div>
                      <div className="staking-info-row">
                        <span>Max Amount:</span>
                        <span>${formatNumber(selectedPlan.max)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Referrer ID for new users */}
                  {isNewUser && (
                    <div className="staking-form-group">
                      <label className="staking-form-label">
                        Referrer ID {defaultReferralId ? '(Pre-filled)' : '(Optional)'}
                      </label>
                      <div className="staking-input-wrapper">
                        <input
                          type="text"
                          placeholder="Enter referrer ID (optional)"
                          value={referrerId}
                          onChange={(e) => setReferrerId(e.target.value)}
                          className={`staking-referrer-input ${errors.referrer ? 'error' : ''}`}
                        />
                      </div>
                      
                      {errors.referrer && (
                        <div className="staking-field-error">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"/>
                            <line x1="12" y1="8" x2="12" y2="12"/>
                            <line x1="12" y1="16" x2="12.01" y2="16"/>
                          </svg>
                          {errors.referrer}
                        </div>
                      )}

                      <div className="staking-referrer-note">
                        {defaultReferralId ? (
                          <p>You're joining through a referral link! Your referrer ID is pre-filled.</p>
                        ) : (
                          <p>If you don't have a referrer ID, leave this empty for automatic assignment.</p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Investment Summary */}
                  <div className="staking-form-group">
                    <label className="staking-form-label">Investment Summary</label>
                    <div className="staking-summary-card">
                      <div className="staking-summary-row">
                        <span>Plan:</span>
                        <span>{selectedPlan.name}</span>
                      </div>
                      <div className="staking-summary-row">
                        <span>USD Amount:</span>
                        <span>${stakeAmount || '0'}</span>
                      </div>
                      <div className="staking-summary-row">
                        <span>Tokens Required:</span>
                        <span>{formatNumber(getRequiredTokens(), 2)} TRAIL</span>
                      </div>
                      <div className="staking-summary-row">
                        <span>Reward Rate:</span>
                        <span>{selectedPlan.roi}</span>
                      </div>
                      <div className="staking-summary-row">
                        <span>Duration:</span>
                        <span>{selectedPlan.duration} {selectedPlan.duration > 100 ? 'days' : ''}</span>
                      </div>
                      <div className="staking-summary-row total">
                        <span>Potential Return:</span>
                        <span>~${formatNumber(calculatePotentialReturn(), 2)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="staking-action-buttons">
                    <button
                      className="staking-back-btn"
                      onClick={handleCloseModal}
                      disabled={loading}
                    >
                      Cancel
                    </button>

                    {needsApproval ? (
                      <button
                        className="staking-approve-btn"
                        onClick={handleApprove}
                        disabled={loading || !stakeAmount || errors.amount}
                      >
                        {loading ? (
                          <span className="staking-btn-content">
                            <span className="staking-loading-spinner"></span>
                            Approving...
                          </span>
                        ) : (
                          <span className="staking-btn-content">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M9 12l2 2 4-4"/>
                              <circle cx="12" cy="12" r="10"/>
                            </svg>
                            Approve {formatNumber(getRequiredTokens(), 2)} TRAIL
                          </span>
                        )}
                      </button>
                    ) : (
                      <button
                        className="staking-stake-btn"
                        onClick={handleStake}
                        disabled={loading || !stakeAmount || Object.keys(errors).length > 0}
                      >
                        {loading ? (
                          <span className="staking-btn-content">
                            <span className="staking-loading-spinner"></span>
                            {isNewUser ? 'Registering & Investing...' : 'Processing Investment...'}
                          </span>
                        ) : (
                          <span className="staking-btn-content">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <line x1="12" y1="5" x2="12" y2="19"/>
                              <line x1="5" y1="12" x2="19" y2="12"/>
                            </svg>
                            {isNewUser ? 'Register & Invest' : 'Complete Investment'}
                          </span>
                        )}
                      </button>
                    )}
                  </div>

                  {needsApproval && (
                    <div className="staking-approval-notice">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"/>
                        <line x1="12" y1="8" x2="12" y2="12"/>
                        <line x1="12" y1="16" x2="12.01" y2="16"/>
                      </svg>
                      <p>First, approve the contract to spend your tokens, then complete your investment.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StakingForm;